import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Users, ExternalLink,
  Instagram, Youtube, Globe, ShieldCheck,
  MapPin, Store,
} from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useBusinessProfile } from '../../hooks/useBusinessProfile';
import ProductCard from './ProductCard';
import ProductDetailView from './ProductDetailView';

// ---------------------------------------------------------------------------
// Analytics — fire-and-forget
// ---------------------------------------------------------------------------
async function logProfileView(businessId) {
  if (!businessId) return;
  try {
    await addDoc(collection(db, 'profileViews'), {
      businessId,
      uid: auth.currentUser?.uid || null,
      timestamp: serverTimestamp(),
    });
  } catch (_) { /* silent */ }
}

async function logBrandLinkClick(businessId, linkType) {
  if (!businessId) return;
  try {
    await addDoc(collection(db, 'brandLinkClicks'), {
      businessId,
      linkType,
      uid: auth.currentUser?.uid || null,
      timestamp: serverTimestamp(),
    });
  } catch (_) { /* silent */ }
}

// ---------------------------------------------------------------------------
// SocialLink — renders a single social/website link button
// ---------------------------------------------------------------------------
const SocialLink = ({ href, icon: Icon, label, businessId, linkType }) => {
  if (!href) return null;
  const handleClick = () => {
    logBrandLinkClick(businessId, linkType);
    window.open(href, '_blank', 'noopener,noreferrer');
  };
  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-xs px-md py-sm rounded-large border border-text-tertiary/20 text-text-secondary hover:border-primary hover:text-primary transition-all text-caption font-semibold"
    >
      <Icon size={14} />
      {label}
    </button>
  );
};

// ---------------------------------------------------------------------------
// TikTok icon — not in lucide, inline SVG
// ---------------------------------------------------------------------------
const TikTokIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
  </svg>
);

// ---------------------------------------------------------------------------
// BrandProfileView
// ---------------------------------------------------------------------------
const BrandProfileView = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { profile, products, loading, error } = useBusinessProfile(businessId);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Log profile view on mount
  useEffect(() => {
    if (businessId) logProfileView(businessId);
  }, [businessId]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-md" />
        <p className="text-body text-text-secondary">Loading brand...</p>
      </div>
    );
  }

  // ── Error / not found ────────────────────────────────────────────────────
  if (error || !profile) {
    return (
      <div className="flex flex-col items-center justify-center py-xxxl text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-md">
          <Package size={28} className="text-primary" />
        </div>
        <h2 className="text-title-3 text-text-primary mb-sm">Brand not found</h2>
        <p className="text-body text-text-secondary mb-lg">
          This brand may no longer be available on Nutrius.
        </p>
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-sm px-lg py-sm bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all"
        >
          <ArrowLeft size={15} />
          Back to Products
        </button>
      </div>
    );
  }

  const socials = profile.socials || {};
  const hasSocials = socials.instagram || socials.tiktok || socials.youtube ||
                     socials.facebook || socials.twitter || profile.website;

  const storeLocations = profile.storeLocations || [];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full">

        {/* Back nav */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-sm text-text-secondary hover:text-primary transition-colors mb-lg text-callout font-semibold"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* ── Cover + profile header ── */}
        <div
          className="rounded-large overflow-hidden mb-lg"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        >
          {/* Cover photo */}
          <div
            className="w-full bg-gradient-to-br from-accent/20 to-primary/10 flex items-center justify-center"
            style={{ height: '180px' }}
          >
            {profile.coverPhoto ? (
              <img
                src={profile.coverPhoto}
                alt={`${profile.businessName} cover`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full"
                style={{ background: 'linear-gradient(135deg, rgba(var(--color-primary),0.12), rgba(var(--color-accent),0.08))' }}
              />
            )}
          </div>

          {/* Profile info bar */}
          <div className="bg-background px-xl pb-xl">
            <div className="flex flex-col sm:flex-row sm:items-end gap-md -mt-10 sm:-mt-12 mb-md">

              {/* Logo */}
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-large bg-surface flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ border: '3px solid var(--color-background)', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}
              >
                {profile.profilePhoto ? (
                  <img
                    src={profile.profilePhoto}
                    alt={profile.businessName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package size={32} className="text-primary/40" />
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0 pt-sm sm:pt-0 sm:pb-sm">
                <div className="flex items-center gap-sm flex-wrap mb-xs">
                  <h1 className="text-title-2 font-rounded text-primary leading-tight">
                    {profile.businessName}
                  </h1>
                  <span className="flex items-center gap-xs px-sm py-xs bg-success/10 text-success text-footnote font-semibold rounded-small">
                    <ShieldCheck size={11} /> Verified Brand
                  </span>
                </div>

                <div className="flex items-center gap-md flex-wrap">
                  <div className="flex items-center gap-xs text-caption text-text-tertiary">
                    <Package size={12} />
                    <span>{products.length} {products.length === 1 ? 'product' : 'products'}</span>
                  </div>
                  {profile.followerCount > 0 && (
                    <div className="flex items-center gap-xs text-caption text-text-tertiary">
                      <Users size={12} />
                      <span>{profile.followerCount.toLocaleString()} followers</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bio */}
            {profile.bio && (
              <p className="text-body text-text-secondary leading-relaxed mb-md">
                {profile.bio}
              </p>
            )}

            {/* Links */}
            {hasSocials && (
              <div className="flex flex-wrap gap-sm">
                {profile.website && (
                  <SocialLink
                    href={profile.website}
                    icon={Globe}
                    label="Website"
                    businessId={profile.id}
                    linkType="website"
                  />
                )}
                {socials.instagram && (
                  <SocialLink
                    href={`https://instagram.com/${socials.instagram.replace('@', '')}`}
                    icon={Instagram}
                    label="Instagram"
                    businessId={profile.id}
                    linkType="instagram"
                  />
                )}
                {socials.tiktok && (
                  <SocialLink
                    href={`https://tiktok.com/@${socials.tiktok.replace('@', '')}`}
                    icon={TikTokIcon}
                    label="TikTok"
                    businessId={profile.id}
                    linkType="tiktok"
                  />
                )}
                {socials.youtube && (
                  <SocialLink
                    href={socials.youtube}
                    icon={Youtube}
                    label="YouTube"
                    businessId={profile.id}
                    linkType="youtube"
                  />
                )}
                {socials.twitter && (
                  <SocialLink
                    href={`https://twitter.com/${socials.twitter.replace('@', '')}`}
                    icon={ExternalLink}
                    label="X / Twitter"
                    businessId={profile.id}
                    linkType="twitter"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Store Locations ── */}
        {storeLocations.length > 0 && (
          <div
            className="rounded-large bg-surface p-lg mb-lg"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <div className="flex items-center gap-sm mb-md">
              <Store size={15} className="text-primary" />
              <h2 className="text-callout font-semibold text-text-primary">Where to Find Us</h2>
            </div>
            <div className="flex flex-wrap gap-sm">
              {storeLocations.map((location, i) => (
                <div
                  key={i}
                  className="flex items-start gap-sm px-md py-sm rounded-large bg-background"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                >
                  <MapPin size={13} className="text-primary flex-shrink-0 mt-xs" />
                  <div>
                    <p className="text-caption font-semibold text-text-primary">
                      {location.chainName}
                    </p>
                    <p className="text-footnote text-text-tertiary">
                      {location.nationwide
                        ? 'Nationwide'
                        : location.regions?.join(', ')}
                    </p>
                    {location.url && (
                      <button
                        onClick={() => {
                          logBrandLinkClick(profile.id, 'store_location');
                          window.open(location.url, '_blank', 'noopener,noreferrer');
                        }}
                        className="text-footnote text-primary hover:underline mt-xs flex items-center gap-xs"
                      >
                        <ExternalLink size={10} /> View online
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Products ── */}
        <div>
          <h2 className="text-title-3 font-rounded text-primary mb-md">
            Products
          </h2>

          {products.length === 0 ? (
            <div
              className="rounded-large bg-surface p-xxxl text-center"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <Package size={36} className="text-text-tertiary/40 mx-auto mb-md" />
              <p className="text-body text-text-secondary">
                No products listed yet
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-md">
              {products.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Product detail modal */}
      {selectedProduct && (
        <ProductDetailView
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </>
  );
};

export default BrandProfileView;
